import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Modal, Dropdown } from '@lucid/lego';
import type { Prospect } from '../../../../../api/services/lupp/prospect/dto.prospect';
import { useGetUserLocations } from '../../../../../api/services/lupp/locations/locations';
import { useUpdateProspect } from '../../../../../api/services/lupp/prospect/prospect';
import { useUserRole } from '../../../../../hooks/useUserRole';
import { buildProspectPayload } from '../utils';
import styles from './ChangeLocationField.module.scss';

/**
 * Real Auth0 `roles` custom-claim values (not `@lucid/auth`'s `USER_ROLES`
 * authz-service resource codes, e.g. 'Agency-Admin-R4') allowed to reassign
 * a prospect's location.
 */
const LOCATION_EDITOR_ROLES = ['LucidPartners-Agency-Admin', 'LucidPartners-Agency-Manager'];

type ChangeLocationFieldProps = {
  prospect?: Prospect;
  labels?: Record<string, string>;
  theme?: 'light' | 'dark';
  /** Called with a user-facing message after the location is saved; shown as a page banner. */
  onUpdateSuccess?: (message: string) => void;
  /** Called with a user-facing message when saving fails; shown as a page banner. */
  onUpdateError?: (message: string) => void;
};

/**
 * Custom Form field that displays the prospect's current location and lets the
 * user reassign it to one of their own locations via a confirmation modal.
 * Save results are reported through callbacks so the parent can show them in
 * its page-level banner, consistent with other prospect update messages.
 *
 * @component
 * @param {ChangeLocationFieldProps} props - Component props
 * @returns {JSX.Element} Location display with a "Change Location" action and modal
 * @example
 * <ChangeLocationField prospect={prospect} labels={labels} onUpdateError={setErrorMessage} />
 */
const ChangeLocationField: React.FC<ChangeLocationFieldProps> = ({
  prospect,
  labels,
  theme = 'dark',
  onUpdateSuccess,
  onUpdateError,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  // Only the user's own locations: the prospects API rejects a move to any other.
  const { data: locationsData } = useGetUserLocations();
  const { mutate: updateProspect, isPending } = useUpdateProspect();
  const { roles } = useUserRole();
  const canEditLocation = useMemo(
    () => roles.some((role) => LOCATION_EDITOR_ROLES.includes(role)),
    [roles]
  );

  const locationOptions = useMemo(
    () =>
      (locationsData?.items ?? []).map((location) => ({
        value: location.locationIdentifier,
        label: location.city
          ? `${location.locationName ?? location.locationIdentifier}, ${location.city}`
          : (location.locationName ?? location.locationIdentifier),
      })),
    [locationsData?.items]
  );

  const handleOpenModal = () => {
    const currentLocationId =
      prospect?.businessPartnerOrganizationLocationIdentifier ||
      prospect?.locations?.[0]?.locationIdentifier ||
      locationsData?.items.find(
        (location) => location.locationName === prospect?.prospectOwnerLocation
      )?.locationIdentifier ||
      '';
    setSelectedLocationId(currentLocationId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirm = () => {
    if (!prospect?.prospectIdentifier || !selectedLocationId) {
      return;
    }
    const selectedLocation = locationsData?.items.find(
      (location) => location.locationIdentifier === selectedLocationId
    );
    if (!selectedLocation) {
      return;
    }

    // The email cannot be edited via the update API; sending it, even unchanged, is rejected.
    const { prospectEmail: _prospectEmail, ...payloadWithoutEmail } = buildProspectPayload(
      prospect,
      new Date().toISOString(),
      {
        // The update API moves the prospect using this field only; `locations` is display-only.
        businessPartnerOrganizationLocationIdentifier: selectedLocation.locationIdentifier,
        prospectOwnerLocation: selectedLocation.locationName || prospect.prospectOwnerLocation,
        locations: [
          {
            locationIdentifier: selectedLocation.locationIdentifier,
            locationName: selectedLocation.locationName,
          },
        ],
      }
    );

    updateProspect(
      { id: prospect.prospectIdentifier, data: payloadWithoutEmail as Prospect },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          onUpdateSuccess?.(
            labels?.['form.locationUpdateSuccess'] ?? 'Location updated successfully'
          );
        },
        onError: (error: unknown) => {
          // Close the modal so the page banner with the API reason (e.g. 409 duplicate) is visible.
          setIsModalOpen(false);
          onUpdateError?.(
            error instanceof Error && error.message
              ? error.message
              : (labels?.['form.locationUpdateError'] ??
                  'Failed to update location. Please try again.')
          );
        },
      }
    );
  };

  return (
    <Box className={styles.container}>
      <Typography variant="label" size="small" theme={theme} color="gray-3">
        {labels?.['form.prospectLocation'] ?? 'Prospect Location'}
      </Typography>
      <Box mt={8}>
        <Typography variant="body" size="medium" theme={theme}>
          {prospect?.prospectOwnerLocation || '-'}
        </Typography>
      </Box>
      {prospect?.prospectIdentifier &&
        canEditLocation &&
        prospect?.statusName?.toLowerCase() !== 'closed' && (
          <Button
            type="tertiary"
            color="sapphire"
            size="small"
            theme={theme}
            className={styles.changeLocationButton}
            dataTestId="change-location-button"
            gaComponent="prospect-details"
            label={labels?.['form.changeLocation'] ?? 'Change Location'}
            handleClick={handleOpenModal}
          />
        )}
      {isModalOpen && (
        <Modal
          dataTestId="change-location-modal"
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={labels?.['form.changeLocationTitle'] ?? 'Change Prospect Location'}
          theme={theme}
          footer={
            <Box className={styles.footer}>
              <Button
                type="secondary"
                color="neutral"
                size="small"
                theme={theme}
                dataTestId="change-location-cancel-button"
                gaComponent="prospect-details"
                label={labels?.['form.cancel'] ?? 'Cancel'}
                handleClick={handleCloseModal}
                disabled={isPending}
              />
              <Button
                type="primary"
                color="neutral"
                size="small"
                theme={theme}
                dataTestId="change-location-confirm-button"
                gaComponent="prospect-details"
                label={
                  isPending
                    ? (labels?.['form.saving'] ?? 'Saving...')
                    : (labels?.['form.confirmAssignment'] ?? 'Confirm Assignment')
                }
                handleClick={handleConfirm}
                disabled={isPending || !selectedLocationId}
              />
            </Box>
          }
        >
          <Typography
            className={styles.modalDescription}
            variant="body"
            size="small"
            theme={theme}
            color="gray-5"
          >
            {labels?.['form.changeLocationDescription'] ??
              'Select a new location for this prospect. Only locations mapped to your manager profile are shown.'}
          </Typography>
          <Dropdown
            theme={theme}
            label={labels?.['form.targetLocation'] ?? 'Target Location'}
            name="targetLocation"
            options={locationOptions}
            selectedOption={selectedLocationId}
            handleSelect={(value) => setSelectedLocationId(value)}
          />
        </Modal>
      )}
    </Box>
  );
};

export default ChangeLocationField;
